import { User, UserResponse } from '@/types';
import { apiInterceptor } from './ApiInterceptor';
import { URI } from '@/types/enum/api.enum';

class UserService {
  private static instance: UserService;
  private readonly STORAGE_KEY = 'user_data';
  private readonly TOKEN_KEY = 'auth_token';

  private constructor() { }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  private generateInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  public async login(email: string, password: string): Promise<UserResponse> {
    try {
      // First get the token
      const tokenResponse = await apiInterceptor.post<{ token: string }, { email: string; password: string }>(
        URI.LOGIN,
        { email, password }
      );

      if (!tokenResponse.token) {
        throw new Error('No token received');
      }

      // Save token first
      this.saveToken(tokenResponse.token);

      // Then get user details
      const userDetails = await apiInterceptor.get<User>(URI.DETAILS);

      if (userDetails) {
        // Generate initials
        const userWithInitials = {
          ...userDetails,
          token: tokenResponse.token,
          initials: this.generateInitials(
            userDetails.first_name,
            userDetails.last_name
          )
        };

        // Save complete user data
        this.saveUserData(userWithInitials);

        return {
          success: true,
          user: userWithInitials
        };
      }

      throw new Error('Failed to get user details');
    } catch (error) {
      console.error('Login error:', error);
      // Clear any partial data
      this.clearUserData();
      return {
        success: false,
        error: 'Failed to login. Please try again.'
      };
    }
  }

  public async register(userData: Omit<User, 'token' | 'initials'>): Promise<UserResponse> {
    try {
      const response = await apiInterceptor.post<UserResponse, Omit<User, 'token' | 'initials'>>(
        URI.SIGNUP,
        userData,
        {
          timeout: 5000, // 5 second timeout for registration
          headers: {
            'X-Request-Type': 'register'
          }
        }
      );

      if (response.success && response.user) {
        // Generate initials
        response.user.initials = this.generateInitials(
          response.user.first_name,
          response.user.last_name
        );

        // Save user data and token
        this.saveUserData(response.user);
        this.saveToken(response.user.token);
      }

      return response;
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: 'Failed to register. Please try again.'
      };
    }
  }

  public async updateProfile(userData: Partial<User>): Promise<UserResponse> {
    try {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found',
        };
      }

      const response = await apiInterceptor.put<UserResponse, Partial<User>>(
        URI.UPDATE,
        userData,
        {
          timeout: 5000, // 5 second timeout for profile update
          headers: {
            'X-Request-Type': 'profile-update'
          }
        }
      );

      if (response.success && response.user) {
        // Update stored user data
        const currentUser = this.getUserData();
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            ...response.user,
            initials: this.generateInitials(
              response.user.first_name,
              response.user.last_name
            ),
          };
          this.saveUserData(updatedUser);
        }
      }

      return response;
    } catch (error) {
      console.error('Profile update error:', error);
      return {
        success: false,
        error: 'Failed to update profile. Please try again.'
      };
    }
  }

  public async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        await apiInterceptor.post<void, { token: string }>(
          URI.LOGOUT,
          { token },
          {
            timeout: 3000, // 3 second timeout for logout
            headers: {
              'X-Request-Type': 'logout'
            }
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearUserData();
    }
  }

  public getUserData(): User | null {
    if (typeof window === 'undefined') return null;
    const data = localStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  }

  public getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private saveUserData(user: User): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
    }
  }

  private saveToken(token?: string): void {
    if (token && typeof window !== 'undefined') {
      localStorage.setItem(this.TOKEN_KEY, token);
    }
  }

  private clearUserData(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.TOKEN_KEY);
    }
  }

  public isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const userService = UserService.getInstance(); 